import { useEffect, useId, useRef, type ChangeEvent, type HTMLAttributes, type ReactNode } from "react";
import { cn } from "../lib/utils";
import { Annunciator, type AnnunciatorTone } from "./annunciator";
import { Button } from "./button";
import { Cell, type CellState } from "./cell";
import { Segmented } from "./segmented";
import { TransactionLink } from "./transaction-link";

export type SwapMode = "buy" | "sell";
export type SwapSlippageMode = "auto" | "custom";
/** Quote lifecycle owned by the application; the component never prices trades. */
export type SwapQuoteStatus = "empty" | "quoting" | "ready" | "stale" | "unavailable";
/** Caution (orange) or blocking (red) presentation for a value the application has judged. */
export type SwapTone = "stalled" | "failing";

export interface SwapAction {
  label: string;
  disabled?: boolean | undefined;
  /** A wallet request or transaction is in flight; renders the Button loader. */
  busy?: boolean | undefined;
  /** Accessible name of the loader while busy. */
  busyLabel?: string | undefined;
  /** `warning` asks for an explicit confirmation, e.g. high price impact. */
  variant?: "primary" | "warning" | undefined;
}

export interface SwapNotice {
  title: string;
  detail?: ReactNode | undefined;
  tone: AnnunciatorTone;
}

/** Terminal transaction outcome. `unconfirmed`: no receipt inside the submission window; it may still land. */
export type SwapResultStatus = "success" | "failure" | "unconfirmed";

export interface SwapResultAction {
  label: string;
  onClick?: (() => void) | undefined;
  /** Renders the action as a link, e.g. a block explorer transaction page. */
  href?: string | undefined;
}

export interface SwapResult {
  status: SwapResultStatus;
  /** Outcome headline, e.g. "Bought 12,500 SQK" or "Buy failed". */
  title: string;
  /** Cause or next step, e.g. "Price moved beyond 2.5% slippage." */
  message?: ReactNode | undefined;
  /** Settled facts for a success (decoded from the receipt, never the quote); attempted values for a failure. */
  details: readonly { label: string; value: ReactNode }[];
  /** The transaction this outcome reports; always rendered as a block explorer link. */
  transaction: { hash: string; href: string };
  primaryAction: SwapResultAction;
  secondaryAction?: SwapResultAction | undefined;
}

const RESULT_PRESENTATION: Record<SwapResultStatus, { hue: string; cell: CellState; role: "status" | "alert" }> = {
  success: { hue: "var(--sqk-green)", cell: "welded", role: "status" },
  failure: { hue: "var(--sqk-red)", cell: "error", role: "alert" },
  unconfirmed: { hue: "var(--sqk-orange)", cell: "stale", role: "status" },
};

const SWAP_MODE_OPTIONS = [
  { label: "Buy", value: "buy" },
  { label: "Sell", value: "sell" },
] as const satisfies readonly { label: string; value: SwapMode }[];

const LOCKED_SWAP_MODE_OPTIONS = SWAP_MODE_OPTIONS.map((option) => ({ ...option, disabled: true }));

const DECIMAL_INPUT = /^\d*(?:\.\d*)?$/;

export interface SwapProps extends Omit<HTMLAttributes<HTMLElement>, "onChange"> {
  mode: SwapMode;
  onModeChange: (mode: SwapMode) => void;
  amount: string;
  onAmountChange: (amount: string) => void;
  /** Balance of the token being spent, formatted by the application, e.g. "Balance 0.42 ETH". */
  balance?: string | undefined;
  /** Blocking amount problem, e.g. "Insufficient ETH · balance 0.042". Replaces the balance and marks the field invalid. */
  amountError?: string | undefined;
  /** Application quote values for the current mode and amount; this component never prices trades. */
  amountPaid: string;
  amountReceived: string;
  quoteStatus: SwapQuoteStatus;
  /** Price impact of the current quote, e.g. "0.4%". */
  priceImpact?: string | undefined;
  priceImpactTone?: SwapTone | undefined;
  /** Live, application-fetched slippage value shown by AUTO. */
  autoSlippage: string;
  /** User-entered slippage value retained while CUSTOM is selected. */
  customSlippage: string;
  slippageMode: SwapSlippageMode;
  onSlippageModeChange: (mode: SwapSlippageMode) => void;
  onCustomSlippageChange: (slippage: string) => void;
  /** Blocking custom slippage problem, e.g. "Enter a slippage above 0%". Takes precedence over the warnings. */
  slippageError?: string | undefined;
  /** CUSTOM values above this percent warn that the trade may be frontrun. Defaults to 5. */
  customSlippageWarnAbove?: number | undefined;
  /** Positive CUSTOM values below this percent warn that the trade may fail. Defaults to 0.05. */
  customSlippageWarnBelow?: number | undefined;
  paymentToken?: string | undefined;
  tokenSymbol?: string | undefined;
  action: SwapAction;
  onAction: () => void;
  /** Freezes every input while a wallet request or transaction is in flight. */
  locked?: boolean | undefined;
  /** Condition that blocks or qualifies trading, shown as an Annunciator below the action. */
  notice?: SwapNotice | undefined;
  helperText?: ReactNode | undefined;
  /** Transaction outcome; takes over the whole component until the application clears it. */
  result?: SwapResult | undefined;
}

function tokenBadge(symbol: string): { mark: string; className: string } {
  return symbol === "SQK"
    ? { mark: "S", className: "sqk-swap__token-mark sqk-swap__token-mark--sqk" }
    : { mark: symbol.slice(0, 1), className: "sqk-swap__token-mark" };
}

function quoteValue(status: SwapQuoteStatus, value: string | undefined): string {
  if (status === "quoting") return "Quoting";
  if (status === "unavailable") return "Unknown";
  if (status === "empty") return "—";
  return value || "—";
}

export function Swap({
  mode,
  onModeChange,
  amount,
  onAmountChange,
  balance,
  amountError,
  amountPaid,
  amountReceived,
  quoteStatus,
  priceImpact,
  priceImpactTone,
  autoSlippage,
  customSlippage,
  slippageMode,
  onSlippageModeChange,
  onCustomSlippageChange,
  slippageError,
  customSlippageWarnAbove = 5,
  customSlippageWarnBelow = 0.05,
  paymentToken = "ETH",
  tokenSymbol = "SQK",
  action,
  onAction,
  locked = false,
  notice,
  helperText,
  result,
  className,
  ...props
}: SwapProps) {
  const id = useId();
  const resultPrimary = useRef<HTMLButtonElement>(null);
  const resultKey = result ? `${result.status}:${result.title}` : null;

  // Move focus into the takeover so keyboard and screen-reader users land on its next action.
  useEffect(() => {
    if (resultKey !== null) resultPrimary.current?.focus({ preventScroll: true });
  }, [resultKey]);
  const payToken = mode === "buy" ? paymentToken : tokenSymbol;
  const receiveToken = mode === "buy" ? tokenSymbol : paymentToken;
  const amountBadge = tokenBadge(tokenSymbol);
  const payBadge = tokenBadge(payToken);
  const verb = mode === "buy" ? "Buy" : "Sell";

  const custom = slippageMode === "custom";
  const customValue = Number(customSlippage);
  const slippageMessage: { tone: SwapTone; text: string } | null = !custom
    ? null
    : slippageError
      ? { tone: "failing", text: slippageError }
      : customValue > customSlippageWarnAbove
        ? { tone: "stalled", text: `Slippage above ${customSlippageWarnAbove}% · may be frontrun` }
        : customValue > 0 && customValue < customSlippageWarnBelow
          ? { tone: "stalled", text: `Slippage below ${customSlippageWarnBelow}% · may fail` }
          : null;

  const changeAmount = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.currentTarget.value;
    if (DECIMAL_INPUT.test(next)) onAmountChange(next);
  };

  const changeCustomSlippage = (event: ChangeEvent<HTMLInputElement>) => {
    const next = event.currentTarget.value;
    if (DECIMAL_INPUT.test(next)) onCustomSlippageChange(next);
  };

  return (
    <section className={cn("sqk-swap", className)} aria-label={`${verb} ${tokenSymbol}`} data-locked={locked} data-result={result?.status} {...props}>
      <div className="sqk-swap__body" inert={result !== undefined}>
        <Segmented
          className="sqk-swap__modes"
          data-mode={mode}
          label="Trade direction"
          onValueChange={onModeChange}
          options={locked ? LOCKED_SWAP_MODE_OPTIONS : SWAP_MODE_OPTIONS}
          value={mode}
        />
  
        <label className="sqk-swap__amount" data-invalid={amountError !== undefined} htmlFor={`${id}-amount`}>
          <span className="sqk-swap__amount-head">
            <span className="sqk-swap__field-label">{verb} {tokenSymbol}</span>
            {amountError ?? balance ? (
              <span className="sqk-swap__amount-status" data-tone={amountError ? "failing" : undefined} id={`${id}-amount-status`}>
                {amountError ?? balance}
              </span>
            ) : null}
          </span>
          <span className="sqk-swap__amount-row">
            <input
              aria-describedby={amountError ?? balance ? `${id}-amount-status` : undefined}
              aria-invalid={amountError ? true : undefined}
              aria-label={`${verb} amount in ${tokenSymbol}`}
              autoComplete="off"
              disabled={locked}
              id={`${id}-amount`}
              inputMode="decimal"
              onChange={changeAmount}
              placeholder="0"
              value={amount}
            />
            <span className="sqk-swap__token">
              <span className={amountBadge.className} aria-hidden="true">{amountBadge.mark}</span>
              <strong>{tokenSymbol}</strong>
            </span>
          </span>
        </label>
  
        <div
          aria-hidden={mode !== "buy"}
          className="sqk-swap__pay-with"
          data-visible={mode === "buy"}
        >
          <span>Pay with</span>
          <span className="sqk-swap__token">
            <span className={payBadge.className} aria-hidden="true">{payBadge.mark}</span>
            <strong>{payToken}</strong>
          </span>
        </div>
  
        <div className="sqk-swap__slippage">
          <div className="sqk-swap__settings">
            <span className="sqk-swap__settings-label">Slippage</span>
            <div aria-label="Slippage setting" className="sqk-swap__slippage-control" data-mode={slippageMode} role="group">
              <Button
                aria-pressed={slippageMode === "auto"}
                className="sqk-swap__slippage-choice sqk-swap__slippage-choice--auto"
                disabled={locked}
                onClick={() => onSlippageModeChange("auto")}
                size="small"
                variant="quiet"
              >
                <span aria-hidden="true" className="sqk-swap__slippage-pill" />
                <span>AUTO</span>
                <span aria-hidden={slippageMode !== "auto"} className="sqk-swap__slippage-detail">
                  <span aria-hidden="true" className="sqk-swap__slippage-separator">·</span>
                  <span className="sqk-swap__slippage-value">{autoSlippage}%</span>
                </span>
              </Button>
              <Button
                aria-pressed={custom}
                className="sqk-swap__slippage-choice sqk-swap__slippage-choice--custom"
                disabled={locked}
                onClick={() => onSlippageModeChange("custom")}
                size="small"
                variant="quiet"
              >
                <span aria-hidden="true" className="sqk-swap__slippage-pill" />
                <span>CUSTOM</span>
              </Button>
              <label
                aria-hidden={!custom}
                className="sqk-swap__custom-slippage"
                data-tone={slippageMessage?.tone}
              >
                <input
                  aria-describedby={slippageMessage ? `${id}-slippage-message` : undefined}
                  aria-invalid={slippageMessage?.tone === "failing" ? true : undefined}
                  aria-label="Custom slippage percent"
                  disabled={!custom || locked}
                  inputMode="decimal"
                  onChange={changeCustomSlippage}
                  value={customSlippage}
                />
                <span>%</span>
              </label>
            </div>
          </div>
  
          <dl className="sqk-swap__quote" aria-label="Swap quote" data-status={quoteStatus}>
            <div><dt>You pay</dt><dd>{quoteValue(quoteStatus, amountPaid)}{" "}<span>{payToken}</span></dd></div>
            <div><dt>You receive</dt><dd>{quoteValue(quoteStatus, amountReceived)}{" "}<span>{receiveToken}</span></dd></div>
            <div><dt>Price impact</dt><dd data-tone={quoteStatus === "ready" || quoteStatus === "stale" ? priceImpactTone : undefined}>{quoteValue(quoteStatus, priceImpact)}</dd></div>
          </dl>
  
          <p
            aria-hidden={!slippageMessage}
            className="sqk-swap__slippage-message"
            data-tone={slippageMessage?.tone}
            data-visible={slippageMessage !== null}
            id={`${id}-slippage-message`}
          >
            {slippageMessage?.text}
          </p>
        </div>
  
        <Button
          busy={action.busy ?? false}
          busyLabel={action.busyLabel ?? action.label}
          className="sqk-swap__action"
          disabled={action.disabled}
          onClick={onAction}
          size="large"
          variant={action.variant ?? "primary"}
        >
          {action.label}
        </Button>
        {notice ? (
          <Annunciator className="sqk-swap__notice" title={notice.title} tone={notice.tone}>
            {notice.detail}
          </Annunciator>
        ) : null}
        {helperText ? <p className="sqk-swap__helper">{helperText}</p> : null}
      </div>

      {result ? (
        <div
          aria-labelledby={`${id}-result-title`}
          className="sqk-swap__result"
          data-status={result.status}
          key={resultKey}
          role={RESULT_PRESENTATION[result.status].role}
        >
          <div className="sqk-swap__result-head">
            <Cell aria-hidden="true" hue={RESULT_PRESENTATION[result.status].hue} role="presentation" state={RESULT_PRESENTATION[result.status].cell} />
            <h3 id={`${id}-result-title`}>{result.title}</h3>
          </div>
          {result.message ? <p className="sqk-swap__result-message">{result.message}</p> : null}
          <dl className="sqk-swap__result-details">
            {result.details.map((detail) => (
              <div key={detail.label}><dt>{detail.label}</dt><dd>{detail.value}</dd></div>
            ))}
            <div><dt>Transaction</dt><dd><TransactionLink hash={result.transaction.hash} href={result.transaction.href} /></dd></div>
          </dl>
          <div className="sqk-swap__result-actions">
            <Button onClick={result.primaryAction.onClick} ref={resultPrimary} size="large" variant="primary">
              {result.primaryAction.label}
            </Button>
            {result.secondaryAction?.href ? (
              <a className="sqk-button" data-size="large" data-variant="quiet" href={result.secondaryAction.href} onClick={result.secondaryAction.onClick} rel="noreferrer" target="_blank">
                {result.secondaryAction.label}
              </a>
            ) : result.secondaryAction ? (
              <Button onClick={result.secondaryAction.onClick} size="large" variant="quiet">
                {result.secondaryAction.label}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
